import {
  Box,
  Center,
  createStyles,
  Divider,
  Pagination,
  Skeleton,
  Stack,
  Text as MantineText,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconClipboardCheck } from '@tabler/icons';
import { FC, Fragment, useContext } from 'react';
import TaskListItem from '../../Molecules/Event/Tasks/TaskListItem';
import { TaskListContext } from '../../Service/Context/TaskListContext';

const useStyles = createStyles((theme) => ({
  page: {
    width: '100%',
    maxWidth: 1120,
    margin: '0 auto',
    padding: `${theme.spacing.xl}px ${theme.spacing.xl}px ${theme.spacing.xl * 2}px`,

    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      padding: 0,
    },
  },

  divider: {
    borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2],
    marginLeft: theme.spacing.xl + 30 + theme.spacing.md,

    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      marginLeft: 28 + theme.spacing.sm,
    },
  },

  placeholderItem: {
    padding: `${theme.spacing.lg}px ${theme.spacing.xl}px`,

    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      padding: `${theme.spacing.md}px 0`,
    },
  },

  placeholderGrid: {
    display: 'grid',
    gridTemplateColumns: '30px minmax(0, 1fr) 34px',
    gap: theme.spacing.md,

    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      gridTemplateColumns: '28px minmax(0, 1fr) 34px',
      gap: theme.spacing.sm,
    },
  },

  emptyState: {
    minHeight: 330,
    padding: `${theme.spacing.xl * 2}px ${theme.spacing.md}px`,
    textAlign: 'center',
  },

  emptyIcon: {
    color: theme.colorScheme === 'dark' ? theme.colors.blue[2] : theme.colors.blue[6],
    backgroundColor: theme.colorScheme === 'dark' ? theme.fn.rgba(theme.colors.blue[6], 0.14) : theme.colors.blue[0],
  },

  emptyTitle: {
    color: theme.colorScheme === 'dark' ? theme.white : theme.colors.dark[8],
  },

  paginationArea: {
    paddingTop: theme.spacing.xl,
  },
}));

const AllTasksList: FC = () => {
  const { classes } = useStyles();
  const { totalPages, taskListFilter, taskListContent, changeFilter, loading } = useContext(TaskListContext);
  const tasks = taskListContent.agendaItems;

  const onChange = (page: number) => changeFilter({ page: page - 1 });

  return (
    <Box className={classes.page}>
      <Box>
        {loading && (
          <Stack spacing={0} aria-label="Aufgaben werden geladen">
            {Array.from({ length: 4 }, (_, index) => (
              <Fragment key={index}>
                <PlaceholderItem />
                {index < 3 && <Divider className={classes.divider} />}
              </Fragment>
            ))}
          </Stack>
        )}

        {!loading && tasks.length > 0 && (
          <Stack spacing={0}>
            {tasks.map((task, index) => (
              <Fragment key={task.id}>
                <TaskListItem task={task} entityId={parseInt(task.entityId, 10)} />
                {index < tasks.length - 1 && <Divider className={classes.divider} />}
              </Fragment>
            ))}
          </Stack>
        )}

        {!loading && tasks.length === 0 && <EmptyTaskList />}
      </Box>

      {!loading && totalPages > 1 && (
        <Center className={classes.paginationArea}>
          <Stack spacing="xs" align="center">
            <Pagination
              size="sm"
              radius="xl"
              total={totalPages}
              page={taskListFilter.page + 1}
              onChange={onChange}
              color="blue"
              siblings={1}
              boundaries={1}
            />
            <MantineText color="dimmed" size="xs">
              Seite {taskListFilter.page + 1} von {totalPages}
            </MantineText>
          </Stack>
        </Center>
      )}
    </Box>
  );
};

const PlaceholderItem: FC = () => {
  const { classes } = useStyles();

  return (
    <Box className={classes.placeholderItem}>
      <Box className={classes.placeholderGrid}>
        <Skeleton circle height={26} />
        <Box>
          <Skeleton height={11} radius="xl" width="44%" />
          <Skeleton height={8} mt="sm" radius="xl" width="68%" />
          <Skeleton height={8} mt="md" radius="xl" width="36%" />
        </Box>
        <Skeleton circle height={30} />
      </Box>
    </Box>
  );
};

const EmptyTaskList: FC = () => {
  const { classes } = useStyles();

  return (
    <Center className={classes.emptyState}>
      <Stack align="center" spacing="xs" maw={360}>
        <ThemeIcon className={classes.emptyIcon} size={56} radius="xl" variant="light" mb="sm">
          <IconClipboardCheck size={30} stroke={1.6} />
        </ThemeIcon>
        <Title className={classes.emptyTitle} order={3} size={20}>
          Keine Aufgaben gefunden
        </Title>
        <MantineText color="dimmed" size="sm" sx={{ lineHeight: 1.6 }}>
          Passe deine Filter an oder erstelle eine neue Aufgabe, um hier loszulegen.
        </MantineText>
      </Stack>
    </Center>
  );
};

export default AllTasksList;
